import reject from 'lodash/reject';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';
import filter from 'lodash/filter';
import unionBy from 'lodash/unionBy';
import { Mapper } from 'modules/core';
import { resolveErrorMessage } from 'modules/errors';
import {
  fetchTopCandidates,
  patchTopCandidate,
  postTopCandidate,
  deleteTopCandidate,
  fetchSharedTopCandidates,
} from './api';

export const loadTopCandidates = createAsyncThunk(
  'topCandidates/load',
  async ({ producerId, projectId, roleId }) =>
    await fetchTopCandidates(producerId, projectId, roleId),
);

export const loadSharedTopCandidates = createAsyncThunk(
  'topCandidates/loadShared',
  async token => await fetchSharedTopCandidates(token),
);

export const addTopCandidate = createAsyncThunk(
  'topCandidates/create',
  async ({
    producerId,
    projectId,
    roleId,
    talentId,
    audioClipId,
    audioSampleId,
    note,
    source,
  }) =>
    await postTopCandidate(
      producerId,
      projectId,
      roleId,
      talentId,
      audioClipId,
      audioSampleId,
      note,
      source,
    ),
);

export const updateTopCandidate = createAsyncThunk(
  'topCandidates/update',
  async ({
    producerId,
    projectId,
    projectRoleId,
    topCandidateId,
    audioClipId,
    audioSampleId,
    note,
    source,
  }) =>
    await patchTopCandidate(
      producerId,
      projectId,
      projectRoleId,
      topCandidateId,
      audioClipId,
      audioSampleId,
      note,
      source,
    ),
);

export const removeTopCandidate = createAsyncThunk(
  'topCandidates/delete',
  async ({ producerId, projectId, roleId, topCandidateId }) =>
    await deleteTopCandidate(producerId, projectId, roleId, topCandidateId),
);

const topCandidatesSlice = createSlice({
  name: 'topCandidates',
  initialState: {
    error: null,
    loading: false,
    topCandidates: [], // TODO: turn arrays of data into object maps (AHAB-597)
  },
  reducers: {
    removeTopCandidatesError(state) {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadTopCandidates.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadTopCandidates.fulfilled, (state, action) => {
        state.topCandidates = unionBy(
          action.payload.data,
          state.topCandidates,
          'id',
        );
        state.loading = false;
      })
      .addCase(loadTopCandidates.rejected, (state, _) => {
        state.loading = false;
      })
      .addCase(loadSharedTopCandidates.fulfilled, (state, action) => {
        state.topCandidates = unionBy(
          action.payload.data,
          state.topCandidates,
          'id',
        );
      })
      .addCase(loadSharedTopCandidates.rejected, (state, action) => {
        state.error = resolveErrorMessage(action.error);
      })
      .addCase(addTopCandidate.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(addTopCandidate.fulfilled, (state, action) => {
        const newCandidate = action.payload.data;
        state.topCandidates = [...state.topCandidates, newCandidate];
        state.loading = false;
      })
      .addCase(addTopCandidate.rejected, (state, _) => {
        state.loading = false;
      })
      .addCase(updateTopCandidate.fulfilled, (state, action) => {
        const updated = action.payload.data;
        state.topCandidates = Mapper.addOrReplace(state.topCandidates, updated);
        state.error = null;
      })
      .addCase(updateTopCandidate.rejected, (state, action) => {
        state.error = resolveErrorMessage(action.error);
      })
      .addCase(removeTopCandidate.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(removeTopCandidate.fulfilled, (state, action) => {
        const removedCandidateId = action.meta.arg.topCandidateId;
        state.topCandidates = reject(
          state.topCandidates,
          candidate => candidate.id === removedCandidateId,
        );
      });
  },
  selectors: {
    getTopCandidatesError: state => state.error,
    getTopCandidates: state => state.topCandidates,
    getTopCandidatesLoading: state => state.loading,
  },
});

export const { removeTopCandidatesError } = topCandidatesSlice.actions;
export const reducer = topCandidatesSlice.reducer;
export const {
  getTopCandidates,
  getTopCandidatesError,
  getTopCandidatesLoading,
} = topCandidatesSlice.selectors;
export const getTopCandidatesByRoleId = roleId =>
  createSelector(
    getTopCandidates,
    topCandidates =>
      filter(
        topCandidates,
        topCandidate => topCandidate.projectRoleId === roleId,
      ),
  );
